import React, { useCallback, useEffect, useRef, useState } from 'react'
import { RequestItemModel } from './requestsModel'
import RequestList from './list/RequestList'
import { useIntl } from 'react-intl'
import {connect, useSelector} from 'react-redux'
import errorCatcher from '../../utils/errorCatcher'
import { RootState } from '../reducers'
import Questions from './questions/Questions'
import DownloadFiles from './downloadFiles/DownloadFiles'
import fetchFactory from '../../utils/fetchFactory'
import { ModelCategory } from '../../types/faq/faq_model'
import { saveFaq } from '../reducers/faq/actions'
import { FilesModel } from './downloadFiles/filesModel'
import { saveFiles } from '../reducers/youfirstFiles/actions'
import Loader from '../../components/loader/Loader'
import FooterNewDemande from './FooterNewDemande'
import { convertRequestList } from './requestMapper'
import { convertFileList } from './downloadFiles/filesMapper'
import { UserModel } from '../../types/user/userModel'
import { saveRequests } from '../reducers/requests/actions'
import ChangementAppartement from '../changementAppartement/ChangementAppartement'
import ChangementAppartementEnCours from '../changementAppartement/ChangementAppartementEnCours'
import possedeDossierValide from '../../utils/possedeDossierValide'
import portals from '../../enums/portalsEnum'

interface Store {
	user: UserModel,
	categories: ModelCategory[],
	saveFaq: (categories: ModelCategory[]) => void,
	files: FilesModel[],
	saveFiles: (files: FilesModel[]) => void,
	errorCatcher: (error: any, id: string) => void
	displayModal: (isOpen: boolean, type: string) => void,
	requests: RequestItemModel[],
	saveRequests: (request: RequestItemModel[]) => void
}

const FAQ_ERROR = 'faq'
const REQUESTS_ERROR = 'get_requests'

type RequestsProps = Store

const Requests: React.FC<RequestsProps> = (
    {
        user,
        categories,
        saveFaq,
        files,
        saveFiles,
        errorCatcher,
        requests,
        saveRequests
    }
) => {
    const intl = useIntl()
    const oldLanguage = useRef(intl.locale)

    const [loadingFaq, setLoadingFaq] = useState<boolean>(true)
    const [loadingFiles, setLoadingFiles] = useState<boolean>(true)
    const [loadingRequests, setLoadingRequests] = useState<boolean>(false)
	const dossier = useSelector((state: RootState) => state.dossier)

	useEffect(
		() => {
			if (categories.length === 0 || oldLanguage.current !== intl.locale) {
				setLoadingFaq(true)
				const fd = new FormData()
				fd.append('route', `/${intl.locale}/api/content/faq?reference=${process.env.REACT_APP_TARGET}`)

				fetchFactory(
					'/api/drupalREST',
					{
						method: 'POST',
						body: fd
					}
				)
					.then(
						(response: any) => {
							saveFaq(response[0].categories)
						}
					)
					.catch(
						(error) => {
							errorCatcher(error, FAQ_ERROR)
						}
					)
					.finally(
						() => {
							setLoadingFaq(false)
						}
					)
			} else {
				setLoadingFaq(false)
			}

			if (files.length === 0 || oldLanguage.current !== intl.locale) {
				setLoadingFiles(true)

				fetchFactory(`/api/ec_${process.env.REACT_APP_TARGET}/information/getDocuments?language=${intl.locale}`)
					.then(
						(response: any) => {
							saveFiles(convertFileList(response))
							oldLanguage.current = intl.locale
						}
					)
					.finally(
						() => {
							setLoadingFiles(false)
						}
					)
			} else {
				setLoadingFiles(false)
			}
		}, [categories.length, errorCatcher, files.length, intl.locale, saveFaq, saveFiles]
	)

	const fetchRequestsList = useCallback(() => {
		setLoadingRequests(true)
		fetchFactory(
			`/api/ec_${process.env.REACT_APP_TARGET}/demande/getRequestList${user.dateDeModification ? '?date=' + user.dateDeModification : ''}`,
			{
				method: 'GET'
			}
		)
			.then(
				(response: any) => {
					if (response && response.value) {
						saveRequests(convertRequestList(response.value))
					}
				}
			)
			.catch(
				(error) => {
					errorCatcher(error, REQUESTS_ERROR)
				}
			)
			.finally(
				() => {
					setLoadingRequests(false)
				}
			)
	}, [errorCatcher, user.dateDeModification, saveRequests])



    return (
        <>
            {
                 (!loadingFaq && !loadingFiles && !loadingRequests) &&
				<>
					{process.env.REACT_APP_TARGET === portals.residence && possedeDossierValide(dossier) && <ChangementAppartementEnCours />}
					<RequestList requests={requests} fetchRequestsList={fetchRequestsList} />
                    {requests.length === 0 && <FooterNewDemande fetchRequestsList={fetchRequestsList} />}
					{process.env.REACT_APP_TARGET === portals.residence && !possedeDossierValide(dossier) && <ChangementAppartement />}
					<Questions categories={categories} />
					<DownloadFiles files={files} />
				</>
            }
            {
                (loadingFaq || loadingFiles || loadingRequests) &&
				<Loader />
            }
        </>
    )
}

const mapStateToProps = (state: RootState) =>
	(
		{
			categories: state.faq,
			files: state.youfirstFiles,
			user: state.user,
			requests: state.requests
		}
	)

const mapDispatchToProps = {
	errorCatcher,
	saveFaq,
	saveFiles,
	saveRequests
}

export default connect(mapStateToProps, mapDispatchToProps)(Requests)
